import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import {
  RefreshCw, Loader2, Activity, AlertTriangle, Power,
  ArrowUp, ArrowDown,
} from 'lucide-react';
import {
  automationService,
  type PaperTradingHealth,
  type PaperTradingAccount,
  type PaperTradingPosition,
  type PaperTradingOrder,
  type PaperTradingTrade,
  type PaperTradingByRule,
  type PaperTradingSettings,
  type Strategy,
  type RulesPage,
  type RuleSortBy,
  type SortDir,
} from '@/services/strategyService';

// ── Helpers ──────────────────────────────────────────────────────

function fmtNY(isoUtc: string | null | undefined, withSeconds = false): string {
  if (!isoUtc) return '-';
  const d = new Date(isoUtc);
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
    ...(withSeconds ? { second: '2-digit' } : {}),
  };
  return new Intl.DateTimeFormat('en-CA', opts).format(d).replace(',', '');
}

function fmtCurrency(n: number | null | undefined, frac = 2): string {
  if (n == null) return '-';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}

function pnlClass(n: number | null | undefined): string {
  if (n == null || n === 0) return '';
  return n > 0 ? 'text-green-500' : 'text-red-500';
}

function shortRuleId(ruleId: string): string {
  if (!ruleId) return '-';
  return ruleId.length > 8 ? ruleId.slice(0, 8) : ruleId;
}

function shortOrderId(orderId: string | null | undefined): string {
  if (!orderId) return '-';
  return orderId.length > 8 ? orderId.slice(0, 8) : orderId;
}

// Treat new-shape brokerReachable + old-shape accountReachable as equivalent.
function isBrokerReachable(h: PaperTradingHealth | null): boolean {
  if (!h) return false;
  if (typeof h.brokerReachable === 'boolean') return h.brokerReachable;
  if (typeof h.accountReachable === 'boolean') return h.accountReachable;
  return false;
}

// ── Hash query helpers ──────────────────────────────────────────

function readHashQuery(): URLSearchParams {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx + 1) : '');
}

function writeHashQuery(params: Record<string, string | null>) {
  const current = readHashQuery();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') current.delete(k);
    else current.set(k, v);
  }
  const hash = window.location.hash;
  const tabPart = hash.split('?')[0] || '#/dashboard/paper-trading';
  const qs = current.toString();
  const newHash = qs ? `${tabPart}?${qs}` : tabPart;
  window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
}

// ── Component ────────────────────────────────────────────────────

// Poll account / positions / orders every 5s. The strategy fires multiple trades
// per minute, so 30s was too coarse — equity/cash/buying-power were stale almost
// every time the user looked. 5s is the sweet spot for a single-user setup
// (well under Alpaca's 200/min rate limit).
const AUTO_REFRESH_MS = 5_000;
const RULE_SORT_VALUES: RuleSortBy[] = ['name', 'createdAt', 'updatedAt', 'enabled'];

function parseRuleSort(v: string | null): RuleSortBy {
  return (RULE_SORT_VALUES as string[]).includes(v ?? '') ? (v as RuleSortBy) : 'createdAt';
}

function parseSortDir(v: string | null): SortDir {
  return v === 'asc' ? 'asc' : 'desc';
}

function parsePageSize(v: string | null): number {
  const n = Number(v);
  return n === 10 || n === 20 || n === 50 ? n : 20;
}

export const PaperTradingPage: React.FC = () => {
  const [health, setHealth] = useState<PaperTradingHealth | null>(null);
  const [account, setAccount] = useState<PaperTradingAccount | null>(null);
  const [positions, setPositions] = useState<PaperTradingPosition[]>([]);
  const [orders, setOrders] = useState<PaperTradingOrder[]>([]);
  const [trades, setTrades] = useState<PaperTradingTrade[]>([]);
  // null = server returned 404 (no creds); [] = creds set, no trades.
  const [byRule, setByRule] = useState<PaperTradingByRule[] | null>([]);

  const [liveRefreshing, setLiveRefreshing] = useState(false);
  const [historyRefreshing, setHistoryRefreshing] = useState(false);

  // Trading Control panel state
  const [settings, setSettings] = useState<PaperTradingSettings | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false); // distinguish "loading" from "no creds"
  const [rulesPage, setRulesPage] = useState<RulesPage | null>(null);
  const [ruleTogglingId, setRuleTogglingId] = useState<string | null>(null);
  const [controlError, setControlError] = useState<string | null>(null);
  // Per-symbol state for the "Close" button on each open position row.
  // null = no close in flight; otherwise the symbol whose close is pending.
  const [closingSymbol, setClosingSymbol] = useState<string | null>(null);

  const [filterRule, setFilterRuleState] = useState<string>(() => {
    return readHashQuery().get('rule') || '';
  });

  // Session filter: '' = all sessions (grouped view), '__today__' = today's ET
  // session, otherwise an ET YYYY-MM-DD string. Default is "today" so the page
  // shows the most operationally-relevant data on first load.
  const [filterSession, setFilterSession] = useState<string>('__today__');

  const setFilterRule = (rule: string) => {
    setFilterRuleState(rule);
    writeHashQuery({ rule: rule || null });
  };

  // Rule pagination state — persisted to URL hash.
  const [rulePage, setRulePageState] = useState<number>(() => {
    const p = Number(readHashQuery().get('rule_page'));
    return p > 0 ? p : 1;
  });
  const [rulePageSize, setRulePageSizeState] = useState<number>(() =>
    parsePageSize(readHashQuery().get('rule_size')),
  );
  const [ruleSortBy, setRuleSortByState] = useState<RuleSortBy>(() =>
    parseRuleSort(readHashQuery().get('rule_sort')),
  );
  const [ruleSortDir, setRuleSortDirState] = useState<SortDir>(() =>
    parseSortDir(readHashQuery().get('rule_dir')),
  );

  const setRulePage = (n: number) => {
    setRulePageState(n);
    writeHashQuery({ rule_page: n === 1 ? null : String(n) });
  };
  const setRulePageSize = (n: number) => {
    setRulePageSizeState(n);
    setRulePageState(1);
    writeHashQuery({ rule_size: n === 20 ? null : String(n), rule_page: null });
  };
  const setRuleSortBy = (s: RuleSortBy) => {
    setRuleSortByState(s);
    setRulePageState(1);
    writeHashQuery({ rule_sort: s === 'createdAt' ? null : s, rule_page: null });
  };
  const setRuleSortDir = (d: SortDir) => {
    setRuleSortDirState(d);
    writeHashQuery({ rule_dir: d === 'desc' ? null : d });
  };

  // Fetch the paginated rule list with current sort/page state.
  const refreshRules = useCallback(async () => {
    try {
      const page = await automationService.getRulesPage({
        page: rulePage,
        pageSize: rulePageSize,
        sortBy: ruleSortBy,
        sortDir: ruleSortDir,
      });
      setRulesPage(page);
    } catch (e: any) {
      setControlError(e?.message || 'Failed to refresh rules');
    }
  }, [rulePage, rulePageSize, ruleSortBy, ruleSortDir]);

  // Refresh live data (health + account + positions + recent orders + settings + rules).
  const refreshLive = useCallback(async () => {
    setLiveRefreshing(true);
    try {
      const [h, a, p, o, s] = await Promise.allSettled([
        automationService.getPaperHealth(),
        automationService.getPaperAccount(),
        automationService.getPaperPositions(),
        automationService.getPaperRecentOrders(50),
        automationService.getPaperSettings(),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value);
      if (a.status === 'fulfilled') setAccount(a.value);
      if (p.status === 'fulfilled') setPositions(p.value || []);
      if (o.status === 'fulfilled') setOrders(o.value || []);
      if (s.status === 'fulfilled') {
        setSettings(s.value);
        setSettingsLoaded(true);
      }
      // Rules fetched separately so it picks up paging changes.
      await refreshRules();
    } finally {
      setLiveRefreshing(false);
    }
  }, [refreshRules]);

  // Per-rule toggle.
  const toggleRule = async (rule: Strategy) => {
    setRuleTogglingId(rule.id);
    setControlError(null);
    try {
      if (rule.enabled) {
        await automationService.disableRule(rule.id);
      } else {
        await automationService.enableRule(rule.id);
      }
      await refreshRules();
    } catch (e: any) {
      setControlError(e?.message || 'Failed to toggle rule');
    } finally {
      setRuleTogglingId(null);
    }
  };

  // Force-close a single open broker position. User clicks the "Close" button
  // on a row → confirm → POST to /api/PaperTrading/positions/{symbol}/close.
  // On success, refresh the live data so the row disappears from the table.
  const closePosition = async (symbol: string) => {
    if (!window.confirm(`Force-close ${symbol} at market? Submits an opposite-side market order on the broker.`)) {
      return;
    }
    setClosingSymbol(symbol);
    try {
      await automationService.closePaperPosition(symbol);
      // Brief delay so the broker has time to ack the order before we refetch
      // (otherwise the position may still appear with full qty).
      setTimeout(() => { void refreshLive(); }, 750);
    } catch (e: any) {
      alert(`Failed to close ${symbol}: ${e?.message || 'unknown error'}`);
    } finally {
      setClosingSymbol(null);
    }
  };

  // Refresh history (recent trades + by-rule). Only manual.
  const refreshHistory = async () => {
    setHistoryRefreshing(true);
    try {
      const [t, r] = await Promise.allSettled([
        automationService.getPaperRecentTrades(100),
        automationService.getPaperTradesByRule(),
      ]);
      if (t.status === 'fulfilled') setTrades(t.value || []);
      if (r.status === 'fulfilled') setByRule(r.value);
    } finally {
      setHistoryRefreshing(false);
    }
  };

  // Initial load — both live and history.
  useEffect(() => {
    refreshLive();
    refreshHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh live data every 30s.
  useEffect(() => {
    const id = setInterval(refreshLive, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [refreshLive]);

  // Refetch rules whenever sort/page changes.
  useEffect(() => {
    void refreshRules();
  }, [refreshRules]);

  // Listen for hash changes (back/forward) to keep filter + paging in sync.
  useEffect(() => {
    const onHash = () => {
      const q = readHashQuery();
      setFilterRuleState(q.get('rule') || '');
      const p = Number(q.get('rule_page'));
      setRulePageState(p > 0 ? p : 1);
      setRulePageSizeState(parsePageSize(q.get('rule_size')));
      setRuleSortByState(parseRuleSort(q.get('rule_sort')));
      setRuleSortDirState(parseSortDir(q.get('rule_dir')));
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Sort by-rule by total PnL desc.
  const sortedByRule = useMemo(() => {
    return [...(byRule ?? [])].sort((a, b) => b.totalPnL - a.totalPnL);
  }, [byRule]);

  // ET-date (YYYY-MM-DD) for a UTC ISO timestamp. ET is UTC-5 in winter and
  // UTC-4 in summer; we use the toLocaleDateString trick with America/New_York
  // tz so DST is handled by the runtime.
  const etDateKey = (utcIso: string): string => {
    if (!utcIso) return '';
    const d = new Date(utcIso);
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(d);
    return parts; // 'YYYY-MM-DD' (en-CA format)
  };

  // Distinct ET session dates present in the trades data, newest first.
  // Used to populate the session-filter dropdown.
  const sessionDates = useMemo(() => {
    const set = new Set<string>();
    for (const t of trades) {
      const d = etDateKey(t.entryTime);
      if (d) set.add(d);
    }
    return Array.from(set).sort().reverse();
  }, [trades]);

  // Today's ET date — used to resolve the '__today__' filter selection.
  const todayEt = useMemo(() => etDateKey(new Date().toISOString()), [trades]);

  // Filter trades by rule and by session date.
  const filteredTrades = useMemo(() => {
    let xs = filterRule ? trades.filter(t => t.ruleId === filterRule) : trades;
    if (filterSession === '__today__') {
      xs = xs.filter(t => etDateKey(t.entryTime) === todayEt);
    } else if (filterSession && filterSession !== '__all__') {
      xs = xs.filter(t => etDateKey(t.entryTime) === filterSession);
    }
    return xs;
  }, [trades, filterRule, filterSession, todayEt]);

  // Session P&L = realized P&L sum across the currently-filtered set. When
  // session filter is "today", this is today's realized P&L. When viewing a
  // historical session, it's that session's realized P&L. When "all", it's
  // the lifetime realized total.
  const sessionPnL = useMemo(() => {
    return filteredTrades.reduce((acc, t) => acc + (t.realizedPnL || 0), 0);
  }, [filteredTrades]);

  // Friendly label for the currently-selected session — used by the stat tile.
  const sessionLabel = useMemo(() => {
    if (filterSession === '__today__') return 'Today';
    if (filterSession === '__all__' || !filterSession) return 'All Sessions';
    return filterSession; // YYYY-MM-DD
  }, [filterSession]);

  // Group filtered trades by ET session date for "all sessions" view.
  // When a specific session is selected, this returns a single group.
  const groupedTrades = useMemo(() => {
    const groups = new Map<string, PaperTradingTrade[]>();
    for (const t of filteredTrades) {
      const k = etDateKey(t.entryTime) || 'Unknown';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(t);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => b.localeCompare(a)); // newest day first
  }, [filteredTrades]);

  // Build a ruleId -> ruleName map from the by-rule summary so we can show
  // friendly names in the trade table and dropdown.
  const ruleNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of (byRule ?? [])) m.set(r.ruleId, r.ruleName || r.ruleId);
    return m;
  }, [byRule]);

  const brokerReachable = isBrokerReachable(health);
  const isLive = !!(health && health.enabled && brokerReachable);

  return (
    <div className="space-y-3 p-5">
      {/* ─── Status Header ─── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Paper Trading
              <span
                className={`ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  isLive
                    ? 'bg-emerald-500/15 text-emerald-500'
                    : 'bg-red-500/15 text-red-500'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${isLive ? 'bg-emerald-500 pulse-live' : 'bg-red-500'}`} />
                {isLive ? 'LIVE' : 'DISABLED'}
              </span>
            </span>
            <span className="text-xs text-muted-foreground font-normal flex items-center gap-2">
              {health?.message && !isLive && (
                <Tooltip content={health.message}>
                  <span className="inline-flex items-center gap-1 text-amber-500">
                    <AlertTriangle className="h-3 w-3" />
                    {health.message.length > 50 ? health.message.slice(0, 50) + '…' : health.message}
                  </span>
                </Tooltip>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={refreshLive}
                disabled={liveRefreshing}
                title="Refresh live data"
              >
                {liveRefreshing
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <RefreshCw className="h-4 w-4" />}
              </Button>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Stat tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <StatTile label="Account" value={account?.accountNumber || '-'} sub={account?.status || ''} />
            <StatTile label="Equity" value={account ? `$${fmtCurrency(account.equity)}` : '-'} mono />
            <StatTile label="Cash" value={account ? `$${fmtCurrency(account.cash)}` : '-'} mono />
            <StatTile label="Buying Power" value={account ? `$${fmtCurrency(account.buyingPower)}` : '-'} mono />
            <StatTile
              label="Day Trades"
              value={account ? String(account.daytradeCount) : '-'}
              sub={account?.patternDayTrader ? 'PDT' : ''}
              mono
            />
            <StatTile
              label={`P&L (${sessionLabel})`}
              value={`${sessionPnL >= 0 ? '+' : ''}$${fmtCurrency(sessionPnL)}`}
              sub={`${filteredTrades.length} trade${filteredTrades.length === 1 ? '' : 's'}`}
              mono
              valueClass={sessionPnL >= 0 ? 'text-emerald-500' : 'text-red-500'}
            />
            <StatTile
              label="Trading"
              value={account?.tradingBlocked ? 'BLOCKED' : (account ? 'Allowed' : '-')}
              valueClass={account?.tradingBlocked ? 'text-red-500' : (account ? 'text-emerald-500' : '')}
            />
          </div>

          {/* Empty-state nudge: no broker creds yet → point at Configuration. */}
          {settingsLoaded && !settings?.apiKey && (
            <div className="mt-3 text-xs text-amber-500 p-3 border border-amber-500/30 rounded-md bg-amber-500/5">
              Set up your broker in{' '}
              <a
                href="#/dashboard/configuration/paper-trading"
                className="underline font-semibold hover:text-amber-400"
              >
                Configuration → Paper Trading
              </a>
              .
            </div>
          )}

          {/* Open positions panel — only if any */}
          {positions.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                Open Positions ({positions.length})
              </div>
              <Card className="overflow-hidden">
                <div
                  className="grid border-b bg-card text-[11px] font-semibold"
                  style={{ gridTemplateColumns: '80px 70px 60px 90px 90px 90px 90px 110px 130px 100px 80px' }}
                >
                  <div className="px-2 py-1.5">Symbol</div>
                  <div className="px-2 py-1.5">Qty</div>
                  <div className="px-2 py-1.5">Side</div>
                  <div className="px-2 py-1.5">Avg Entry</div>
                  <div className="px-2 py-1.5">Current</div>
                  <div className="px-2 py-1.5">U-PL ($)</div>
                  <div className="px-2 py-1.5">U-PL (%)</div>
                  <div className="px-2 py-1.5" title="Cost basis (qty × avg entry)">Cost</div>
                  <div className="px-2 py-1.5" title="Rule that opened this position; '—' if orphan">Rule</div>
                  <div className="px-2 py-1.5" title="Entry time / hold duration in ET">Opened (ET)</div>
                  <div className="px-2 py-1.5"></div>
                </div>
                {positions.map(p => {
                  const openedEt = p.openedAtUtc
                    ? new Date(p.openedAtUtc).toLocaleString('en-US', {
                        timeZone: 'America/New_York',
                        month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit', hour12: false,
                      })
                    : null;
                  const holdMin = p.openedAtUtc
                    ? Math.max(0, (Date.now() - new Date(p.openedAtUtc).getTime()) / 60000)
                    : null;
                  return (
                    <div
                      key={p.symbol}
                      className={`grid border-b text-[11px] ${p.isOrphan ? 'bg-amber-500/5' : ''}`}
                      style={{ gridTemplateColumns: '80px 70px 60px 90px 90px 90px 90px 110px 130px 100px 80px' }}
                    >
                      <div className="px-2 py-1 font-semibold flex items-center gap-1">
                        {p.symbol}
                        {p.isOrphan && (
                          <span title="Orphan: broker holds this but no matching open trade in our DB. Likely a previous exit failed at the broker." className="text-amber-500 text-[10px] font-bold">⚠</span>
                        )}
                      </div>
                      <div className="px-2 py-1 tabular-nums">{p.qty}</div>
                      <div className={`px-2 py-1 ${p.side === 'long' || p.side === 'Long' ? 'text-green-500' : 'text-red-500'}`}>
                        {p.side}
                      </div>
                      <div className="px-2 py-1 tabular-nums">${fmtCurrency(p.avgEntryPrice, 3)}</div>
                      <div className="px-2 py-1 tabular-nums">${fmtCurrency(p.currentPrice, 3)}</div>
                      <div className={`px-2 py-1 tabular-nums ${pnlClass(p.unrealizedPL)}`}>
                        {p.unrealizedPL >= 0 ? '+' : ''}${fmtCurrency(p.unrealizedPL)}
                      </div>
                      <div className={`px-2 py-1 tabular-nums ${pnlClass(p.unrealizedPLPercent)}`}>
                        {p.unrealizedPLPercent >= 0 ? '+' : ''}{p.unrealizedPLPercent.toFixed(2)}%
                      </div>
                      <div className="px-2 py-1 tabular-nums text-muted-foreground">
                        ${fmtCurrency(p.costBasis ?? p.qty * p.avgEntryPrice, 0)}
                      </div>
                      <div className="px-2 py-1 truncate text-muted-foreground" title={p.ruleName ?? ''}>
                        {p.ruleName ?? <span className="text-amber-500">— orphan</span>}
                      </div>
                      <div className="px-2 py-1 text-muted-foreground tabular-nums">
                        {openedEt
                          ? <>{openedEt}<span className="ml-1 text-[10px]">({holdMin! < 60 ? `${holdMin!.toFixed(0)}m` : `${(holdMin! / 60).toFixed(1)}h`})</span></>
                          : <span className="text-amber-500">unknown</span>}
                      </div>
                      <div className="px-2 py-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-red-500 hover:bg-red-500/10 hover:text-red-500"
                          disabled={closingSymbol === p.symbol}
                          onClick={() => closePosition(p.symbol)}
                          title={`Force-close ${p.symbol} via market order`}
                        >
                          {closingSymbol === p.symbol
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : 'Close'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </Card>
            </div>
          )}

          {/* Recent orders preview (small, collapsible feel) */}
          {orders.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                Recent Orders ({orders.length})
              </div>
              <Card className="overflow-hidden max-h-[200px] overflow-y-auto">
                <div
                  className="grid border-b bg-card text-[11px] font-semibold sticky top-0"
                  style={{ gridTemplateColumns: '90px 70px 50px 50px 80px 110px' }}
                >
                  <div className="px-2 py-1.5">Order</div>
                  <div className="px-2 py-1.5">Sym</div>
                  <div className="px-2 py-1.5">Side</div>
                  <div className="px-2 py-1.5">Qty</div>
                  <div className="px-2 py-1.5">Status</div>
                  <div className="px-2 py-1.5">Submitted</div>
                </div>
                {orders.map(o => (
                  <div
                    key={o.orderId}
                    className="grid border-b text-[11px]"
                    style={{ gridTemplateColumns: '90px 70px 50px 50px 80px 110px' }}
                  >
                    <Tooltip content={o.orderId}>
                      <div className="px-2 py-1 truncate font-mono">{shortOrderId(o.orderId)}</div>
                    </Tooltip>
                    <div className="px-2 py-1 font-semibold">{o.symbol}</div>
                    <div className={`px-2 py-1 ${o.side === 'buy' || o.side === 'Buy' ? 'text-green-500' : 'text-red-500'}`}>
                      {o.side}
                    </div>
                    <div className="px-2 py-1 tabular-nums">{o.qty}</div>
                    <div className="px-2 py-1">{o.status}</div>
                    <div className="px-2 py-1">{fmtNY(o.submittedAt)}</div>
                  </div>
                ))}
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Trading Control Panel ─── */}
      <TradingControlPanel
        settings={settings}
        rulesPage={rulesPage}
        sortBy={ruleSortBy}
        sortDir={ruleSortDir}
        page={rulePage}
        pageSize={rulePageSize}
        ruleTogglingId={ruleTogglingId}
        controlError={controlError}
        onRuleToggle={toggleRule}
        onSortByChange={setRuleSortBy}
        onSortDirChange={setRuleSortDir}
        onPageChange={setRulePage}
        onPageSizeChange={setRulePageSize}
      />

      {/* ─── Strategy Summary Table ─── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Strategy Summary</span>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshHistory}
              disabled={historyRefreshing}
              title="Refresh strategy summary + recent trades"
            >
              {historyRefreshing
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RefreshCw className="h-4 w-4" />}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {byRule === null ? (
            <div className="text-xs text-muted-foreground p-4 text-center">
              No paper trades yet — add credentials and enable the kill switch to start trading.
            </div>
          ) : sortedByRule.length === 0 ? (
            <div className="text-xs text-muted-foreground p-4 text-center">
              No enabled rules. Enable a rule on the Automation page to start trading.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div
                className="grid border-b bg-card text-[11px] font-semibold"
                style={{ gridTemplateColumns: 'minmax(180px, 2fr) 70px 70px 70px 100px 90px 80px 90px 90px 90px 80px 110px' }}
              >
                <div className="px-2 py-1.5">Rule</div>
                <div className="px-2 py-1.5" title="Currently held positions">Open</div>
                <div className="px-2 py-1.5">Closed</div>
                <div className="px-2 py-1.5">Win Rate</div>
                <div className="px-2 py-1.5">Total P&L</div>
                <div className="px-2 py-1.5">Avg P&L</div>
                <div className="px-2 py-1.5" title="Avg risk-adjusted return per trade">Avg R</div>
                <div className="px-2 py-1.5">Best</div>
                <div className="px-2 py-1.5">Worst</div>
                <div className="px-2 py-1.5">Avg Hold</div>
                <div className="px-2 py-1.5" title="Broker fees — always 0 for Alpaca paper">Fees</div>
                <div className="px-2 py-1.5">Last Activity</div>
              </div>
              {sortedByRule.map(r => (
                <div
                  key={r.ruleId}
                  className="grid border-b text-[11px] hover:bg-blue-500/5 cursor-pointer"
                  style={{ gridTemplateColumns: 'minmax(180px, 2fr) 70px 70px 70px 100px 90px 80px 90px 90px 90px 80px 110px' }}
                  onClick={() => setFilterRule(filterRule === r.ruleId ? '' : r.ruleId)}
                  title="Click to filter recent trades by this rule"
                >
                  <div className="px-2 py-1 truncate">{r.ruleName || r.ruleId}</div>
                  <div className={`px-2 py-1 tabular-nums ${r.openTrades > 0 ? 'text-emerald-500 font-medium' : 'text-muted-foreground'}`}>
                    {r.openTrades}
                  </div>
                  <div className="px-2 py-1 tabular-nums">
                    {r.totalTrades > 0
                      ? <>{r.totalTrades} <span className="text-muted-foreground">({r.winners}W/{r.losers}L)</span></>
                      : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className="px-2 py-1 tabular-nums">
                    {r.totalTrades > 0 ? `${r.winRate.toFixed(1)}%` : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className={`px-2 py-1 tabular-nums ${pnlClass(r.totalPnL)}`}>
                    {r.totalTrades > 0 ? `${r.totalPnL >= 0 ? '+' : ''}$${fmtCurrency(r.totalPnL)}` : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className={`px-2 py-1 tabular-nums ${pnlClass(r.avgPnL)}`}>
                    {r.totalTrades > 0 ? `${r.avgPnL >= 0 ? '+' : ''}$${fmtCurrency(r.avgPnL)}` : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className={`px-2 py-1 tabular-nums ${pnlClass(r.avgRMultiple)}`}>
                    {r.totalTrades > 0 ? `${r.avgRMultiple >= 0 ? '+' : ''}${r.avgRMultiple.toFixed(2)}R` : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className={`px-2 py-1 tabular-nums ${pnlClass(r.bestTrade)}`}>
                    {r.totalTrades > 0 ? `+$${fmtCurrency(r.bestTrade)}` : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className={`px-2 py-1 tabular-nums ${pnlClass(r.worstTrade)}`}>
                    {r.totalTrades > 0 ? `$${fmtCurrency(r.worstTrade)}` : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className="px-2 py-1 tabular-nums">
                    {r.totalTrades > 0 ? `${r.avgHoldingMinutes.toFixed(1)}m` : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className="px-2 py-1 tabular-nums text-muted-foreground">
                    ${fmtCurrency(r.totalFees)}
                  </div>
                  <div className="px-2 py-1 text-muted-foreground">
                    {r.lastTradeAt
                      ? new Date(r.lastTradeAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : 'never'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Recent Trades Table ─── */}
      <Card className="flex flex-col" style={{ height: 'calc(100vh - 240px)', minHeight: '400px' }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Recent Trades</span>
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground mb-0.5">Session</span>
                <Select
                  value={filterSession || '__all__'}
                  onValueChange={(v) => setFilterSession(v)}
                >
                  <SelectTrigger className="h-8 w-[160px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover/100 backdrop-blur-md z-[100] max-h-[400px]">
                    <SelectItem value="__today__">Today (ET)</SelectItem>
                    <SelectItem value="__all__">All sessions</SelectItem>
                    {sessionDates
                      .filter(d => d !== todayEt)
                      .map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground mb-0.5">Filter by rule</span>
                <Select
                  value={filterRule || '__all__'}
                  onValueChange={(v) => setFilterRule(v === '__all__' ? '' : v)}
                >
                  <SelectTrigger className="h-8 w-[220px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover/100 backdrop-blur-md z-[100] max-h-[400px]">
                    <SelectItem value="__all__">All rules</SelectItem>
                    {sortedByRule.map(r => (
                      <SelectItem key={r.ruleId} value={r.ruleId}>
                        {r.ruleName || shortRuleId(r.ruleId)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshHistory}
                disabled={historyRefreshing}
                title="Refresh recent trades"
                className="self-end"
              >
                {historyRefreshing
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 flex-1 min-h-0 flex flex-col">
          {filteredTrades.length === 0 ? (
            <div className="text-xs text-muted-foreground p-4 text-center">
              {trades.length === 0
                ? 'No closed paper trades yet.'
                : filterSession === '__today__'
                  ? 'No trades yet today (ET).'
                  : 'No trades match the current filters.'}
            </div>
          ) : filterSession === '__all__' && groupedTrades.length > 1 ? (
            // "All sessions" view — render with sticky date headers between groups.
            <div className="flex-1 min-h-0 overflow-y-auto">
              {groupedTrades.map(([sessionDate, sessionTrades]) => {
                const sessionPnLForDay = sessionTrades.reduce((acc, t) => acc + (t.realizedPnL || 0), 0);
                return (
                  <div key={sessionDate} className="mb-3">
                    <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b px-3 py-1.5 flex items-center justify-between">
                      <span className="text-xs font-semibold tabular-nums">{sessionDate} ET</span>
                      <span className="text-xs flex items-center gap-3 tabular-nums">
                        <span className="text-muted-foreground">{sessionTrades.length} trades</span>
                        <span className={sessionPnLForDay >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                          {sessionPnLForDay >= 0 ? '+' : ''}${fmtCurrency(sessionPnLForDay)}
                        </span>
                      </span>
                    </div>
                    <RecentTradesVirtualTable
                      trades={sessionTrades}
                      ruleNameById={ruleNameById}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <RecentTradesVirtualTable
              trades={filteredTrades}
              ruleNameById={ruleNameById}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ── Stat Tile ────────────────────────────────────────────────────

interface StatTileProps {
  label: string;
  value: string;
  sub?: string;
  mono?: boolean;
  valueClass?: string;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, sub, mono, valueClass }) => (
  <div className="rounded-md border bg-card/50 px-3 py-2 flex flex-col">
    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
    <span className={`text-sm font-semibold ${mono ? 'tabular-nums font-mono' : ''} ${valueClass || ''}`}>
      {value}
    </span>
    {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
  </div>
);

// ── Recent Trades Virtualized Table ─────────────────────────────

const TRADE_COL_TEMPLATE = '105px 60px 50px 50px 70px 70px 80px 80px 90px minmax(120px, 1fr)';

interface RecentTradesProps {
  trades: PaperTradingTrade[];
  ruleNameById: Map<string, string>;
}

const RecentTradesVirtualTable: React.FC<RecentTradesProps> = ({ trades, ruleNameById }) => {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: trades.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 26,
    overscan: 12,
  });

  return (
    <div className="flex flex-col flex-1 min-h-0 border rounded-md overflow-hidden">
      <div
        className="grid border-b bg-card text-[11px] font-semibold"
        style={{ gridTemplateColumns: TRADE_COL_TEMPLATE }}
      >
        <div className="px-2 py-1.5">Time (ET)</div>
        <div className="px-2 py-1.5">Symbol</div>
        <div className="px-2 py-1.5">Side</div>
        <div className="px-2 py-1.5">Qty</div>
        <div className="px-2 py-1.5">Entry</div>
        <div className="px-2 py-1.5">Exit</div>
        <div className="px-2 py-1.5">P&L</div>
        <div className="px-2 py-1.5">Status</div>
        <div className="px-2 py-1.5">Order</div>
        <div className="px-2 py-1.5">Rule</div>
      </div>

      <div ref={parentRef} className="overflow-auto flex-1">
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((vi) => {
            const t = trades[vi.index];
            const isWin = (t.realizedPnL || 0) > 0;
            const isLoss = (t.realizedPnL || 0) < 0;
            const ruleName = ruleNameById.get(t.ruleId) || shortRuleId(t.ruleId);
            const sideRaw = (t as any).side || (t as any).direction;
            const side = sideRaw || (t.quantity > 0 ? 'Long' : 'Short');
            return (
              <div
                key={t.id}
                className="grid border-b text-[11px] hover:bg-blue-500/5 whitespace-nowrap"
                style={{
                  gridTemplateColumns: TRADE_COL_TEMPLATE,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${vi.size}px`,
                  transform: `translateY(${vi.start}px)`,
                }}
              >
                <div className="px-2 py-1">{fmtNY(t.entryTime)}</div>
                <div className="px-2 py-1 font-semibold">{t.symbol}</div>
                <div className={`px-2 py-1 ${side === 'Long' || side === 'long' || side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                  {String(side)[0]?.toUpperCase()}
                </div>
                <div className="px-2 py-1 tabular-nums">{t.quantity}</div>
                <div className="px-2 py-1 tabular-nums">{t.entryPrice != null ? `$${fmtCurrency(t.entryPrice, 3)}` : '-'}</div>
                <div className="px-2 py-1 tabular-nums">{t.exitPrice != null ? `$${fmtCurrency(t.exitPrice, 3)}` : '-'}</div>
                <div className={`px-2 py-1 tabular-nums ${isWin ? 'text-green-500' : isLoss ? 'text-red-500' : ''}`}>
                  {(t.realizedPnL ?? 0) >= 0 ? '+' : ''}${fmtCurrency(t.realizedPnL ?? 0)}
                </div>
                <div className="px-2 py-1">
                  {t.brokerOrderError ? (
                    <Tooltip content={t.brokerOrderError}>
                      <span className="text-red-500 inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> {t.status}
                      </span>
                    </Tooltip>
                  ) : (
                    <span>{t.status}</span>
                  )}
                </div>
                <div className="px-2 py-1 font-mono">
                  {t.brokerEntryOrderId ? (
                    <Tooltip content={t.brokerEntryOrderId}>
                      <span>{shortOrderId(t.brokerEntryOrderId)}</span>
                    </Tooltip>
                  ) : '-'}
                </div>
                <Tooltip content={ruleName}>
                  <div className="px-2 py-1 truncate text-muted-foreground">
                    {ruleName.length > 18 ? ruleName.slice(0, 18) + '…' : ruleName}
                  </div>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Trading Control Panel ───────────────────────────────────────

interface TradingControlPanelProps {
  settings: PaperTradingSettings | null;
  rulesPage: RulesPage | null;
  sortBy: RuleSortBy;
  sortDir: SortDir;
  page: number;
  pageSize: number;
  ruleTogglingId: string | null;
  controlError: string | null;
  onRuleToggle: (rule: Strategy) => void;
  onSortByChange: (s: RuleSortBy) => void;
  onSortDirChange: (d: SortDir) => void;
  onPageChange: (p: number) => void;
  onPageSizeChange: (n: number) => void;
}

const TradingControlPanel: React.FC<TradingControlPanelProps> = ({
  settings,
  rulesPage,
  sortBy,
  sortDir,
  page,
  pageSize,
  ruleTogglingId,
  controlError,
  onRuleToggle,
  onSortByChange,
  onSortDirChange,
  onPageChange,
  onPageSizeChange,
}) => {
  const enabled = !!settings?.paperTradingEnabled;

  const items = rulesPage?.items ?? [];
  const total = rulesPage?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const fromIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const toIdx = Math.min(page * pageSize, total);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Power className="h-4 w-4 text-primary" />
            Trading Control
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {controlError && (
          <div className="text-xs text-red-500 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> {controlError}
          </div>
        )}

        {/* ── Rule list header (sort + page-size) ── */}
        <div>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Automation Rules ({total})
            </span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">Sort by</span>
                <Select value={sortBy} onValueChange={(v) => onSortByChange(v as RuleSortBy)}>
                  <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="createdAt">Created</SelectItem>
                    <SelectItem value="updatedAt">Updated</SelectItem>
                    <SelectItem value="enabled">Status</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc')}
                  title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">Page size</span>
                <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
                  <SelectTrigger className="h-7 w-[64px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {!enabled && total > 0 && (
            <div className="text-[11px] text-amber-500 flex items-center gap-1 mb-2">
              <AlertTriangle className="h-3 w-3" />
              Master switch is off — toggling rules will not place real orders.
            </div>
          )}
          {total === 0 ? (
            <div className="text-xs text-muted-foreground p-4 text-center border rounded-md">
              No automation rules yet. Tab to Automation to create one.
            </div>
          ) : (
            <>
              <div className="border rounded-md divide-y overflow-hidden">
                {items.map(rule => (
                  <RuleRow
                    key={rule.id}
                    rule={rule}
                    toggling={ruleTogglingId === rule.id}
                    masterEnabled={enabled}
                    onToggle={() => onRuleToggle(rule)}
                  />
                ))}
              </div>
              {/* Pager */}
              <RulePager
                page={page}
                totalPages={totalPages}
                fromIdx={fromIdx}
                toIdx={toIdx}
                total={total}
                onPageChange={onPageChange}
              />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Rule Pager ──────────────────────────────────────────────────

interface RulePagerProps {
  page: number;
  totalPages: number;
  fromIdx: number;
  toIdx: number;
  total: number;
  onPageChange: (n: number) => void;
}

/**
 * Builds the visible page buttons: always show first, last, current ±1,
 * and gaps via "…". Returns a sequence of numbers and the literal '…'.
 */
function buildPageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
}

const RulePager: React.FC<RulePagerProps> = ({ page, totalPages, fromIdx, toIdx, total, onPageChange }) => {
  const pageList = buildPageList(page, totalPages);
  return (
    <div className="flex items-center justify-between mt-2 text-[11px]">
      <span className="text-muted-foreground">
        Showing {fromIdx}–{toIdx} of {total}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          ‹
        </Button>
        {pageList.map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="px-1 text-muted-foreground">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'default' : 'outline'}
              size="sm"
              className="h-7 min-w-[28px] px-2"
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          ›
        </Button>
      </div>
    </div>
  );
};

// ── Rule Row ───────────────────────────────────────────────────

interface RuleRowProps {
  rule: Strategy;
  toggling: boolean;
  masterEnabled: boolean;
  onToggle: () => void;
}

function fmtTimeOnly(t: string | null | undefined): string {
  if (!t) return '—';
  // "HH:mm:ss" → "HH:mm"
  const m = /^(\d{2}):(\d{2})/.exec(t);
  return m ? `${m[1]}:${m[2]}` : t;
}

const RuleRow: React.FC<RuleRowProps> = ({ rule, toggling, masterEnabled, onToggle }) => {
  const window = `${fmtTimeOnly(rule.entryStartTimeEt)}-${fmtTimeOnly(rule.entryEndTimeEt)}`;
  const meta = `${rule.scannerId} · ${window} · ${rule.maxConcurrentTrades} concurrent`;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-blue-500/5">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{rule.name}</div>
        <Tooltip content={meta}>
          <div className="text-[11px] text-muted-foreground truncate">{meta}</div>
        </Tooltip>
      </div>
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${
          rule.enabled
            ? 'bg-emerald-500/15 text-emerald-500'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${rule.enabled ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
        {rule.enabled ? 'Active' : 'Inactive'}
      </span>
      <Tooltip
        content={
          masterEnabled
            ? (rule.enabled ? 'Disable rule' : 'Enable rule')
            : 'Master switch is off — toggling will not place real orders.'
        }
      >
        <span className="shrink-0">
          <ToggleSwitch
            checked={rule.enabled}
            disabled={toggling}
            loading={toggling}
            onChange={onToggle}
            ariaLabel={`Toggle rule ${rule.name}`}
            size="md"
          />
        </span>
      </Tooltip>
    </div>
  );
};

// ── Toggle Switch primitive (no @radix-ui/react-switch installed) ──

interface ToggleSwitchProps {
  checked: boolean;
  disabled?: boolean;
  loading?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
  size?: 'md' | 'lg';
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  disabled,
  loading,
  onChange,
  ariaLabel,
  size = 'md',
}) => {
  const dims = size === 'lg'
    ? { track: 'h-7 w-12', thumb: 'h-5 w-5', thumbOn: 'translate-x-5', thumbOff: 'translate-x-1' }
    : { track: 'h-5 w-9', thumb: 'h-4 w-4', thumbOn: 'translate-x-4', thumbOff: 'translate-x-0.5' };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex ${dims.track} items-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        checked ? 'bg-emerald-500' : 'bg-muted-foreground/30'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block ${dims.thumb} transform rounded-full bg-white shadow transition-transform ${
          checked ? dims.thumbOn : dims.thumbOff
        } flex items-center justify-center`}
      >
        {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </span>
    </button>
  );
};

export default PaperTradingPage;
